import { Injectable } from '@angular/core';

import { LucideIconData } from 'lucide-angular';

import { BUNDLED_ICON_NAMES, toKebabCase } from './app.icons';

/**
 * Busca iconos de lucide que no están en el catálogo curado.
 *
 * `LucideAngularModule.pick()` sólo empaqueta lo declarado en `app.icons.ts`,
 * así que un nombre escrito a mano (por ejemplo en el campo de icono de un
 * menú) no se dibujaría. Este servicio resuelve ese caso yendo a buscar el set
 * completo al vuelo.
 *
 * Por qué funciona sin engordar el bundle: `lucide-angular/src/icons` es un
 * punto de entrada distinto del que importa `IconsModule`, así que el
 * `import()` dinámico queda en un chunk aparte. Ese chunk (~2,5 MB sin
 * comprimir) no se baja nunca salvo que alguien use un icono fuera del
 * catálogo, y una vez bajado lo cachea el navegador.
 *
 * Por eso el catálogo sigue siendo el camino principal: lo que se use seguido
 * conviene agregarlo ahí, para que salga del bundle y no de este atajo.
 */
@Injectable({ providedIn: 'root' })
export class LucideIconLoaderService {

  /** Promesa del set completo. Se pide una sola vez por sesión. */
  private _allIcons: Promise<Map<string, LucideIconData>> | null = null;

  /** Lo ya resuelto, para no rehacer el lookup en cada render */
  private _cache = new Map<string, LucideIconData | null>();

  /** ¿El icono viene empaquetado en el bundle? */
  isBundled(name?: string): boolean {
    return !!name && BUNDLED_ICON_NAMES.has(name.trim());
  }

  /**
   * Datos de un icono que no está en el catálogo, o null si el nombre no
   * existe en lucide. Quien llame decide qué hacer con el null (normalmente,
   * no dibujar nada).
   * @param name Nombre del icono en kebab ('square-pen')
   */
  async load(name?: string): Promise<LucideIconData | null> {
    const key = (name || '').trim();
    if (!key) return null;
    if (this._cache.has(key)) return this._cache.get(key);

    const icons = await this._loadAll();
    const icon = icons.get(key) ?? null;
    this._cache.set(key, icon);
    return icon;
  }

  /**
   * Trae el set completo y lo indexa por nombre en kebab. El índice se arma
   * con el mismo `toKebabCase` que usa el catálogo, así el nombre que se
   * escribe es el mismo que ofrece el selector y no hay que adivinar cómo
   * se traduce a PascalCase.
   */
  private _loadAll(): Promise<Map<string, LucideIconData>> {
    if (!this._allIcons) {
      this._allIcons = import('lucide-angular/src/icons')
        .then(module => {
          const index = new Map<string, LucideIconData>();
          Object.entries(module.icons || {}).forEach(([pascal, data]) => {
            index.set(toKebabCase(pascal), data as LucideIconData);
          });
          return index;
        })
        .catch(() => new Map<string, LucideIconData>());
    }
    return this._allIcons;
  }
}
